import React, { useState, useMemo, useEffect } from "react";
import queries from "../helpers/queries";
import { FiFileText, FiMonitor, FiX, FiEdit } from "react-icons/fi";
import { useForm } from "react-hook-form";
import { withUser } from "../Contexts/user";
import Select from "react-select";
import Modal from "../Components/modal";
import Table from "../Components/darkBasicTable";
import moment from "moment";
import { useQuery, useMutation } from "@apollo/react-hooks";
import useDimensions from "../Hooks/useDimensions";

const sevenDaysLater = moment().add(7, "days");

const getAgentCommStatusLabel = (row, userId) => {
    if (!row?.members || !Array.isArray(row.members)) {
        return "-";
    }

    for (const item of row.members) {
        if (item?.member?._id === userId) {
            if (item.fullClaimComm != null) {
                return "Full Claim";
            }
            if (item.secondBatchComm != null) {
                return "Second Batch";
            }
            if (item.firstBatchComm != null) {
                return "First Batch";
            }
            const fastComm = item.memberCommStatus?.fastComm;
            if (fastComm === 100) {
                return "Full";
            }
            if (fastComm === 50) {
                return "Half";
            }
            if (fastComm === 0) {
                return "None";
            }
            return "-";
        }
    }

    return "-";
};

const isAgentFastCommSelectable = (row, userId) => {
    const commStatus = getAgentCommStatusLabel(row, userId);
    return (
        commStatus === "-" &&
        row.status === "pending" &&
        moment(row.bookingDate).isAfter("2025-03-16") &&
        moment(row.createdAt).isBefore(sevenDaysLater)
    );
};

const getLeaderUplineCommStatusLabel = (row, userId) => {
    let commStatus = "-";

    if (row?.sponsorCommissions && Array.isArray(row.sponsorCommissions)) {
        for (const item of row.sponsorCommissions) {
            if (item?.user?._id === userId) {
                const fastComm = item.referralCommStatus?.fastComm;
                if (fastComm === 100) {
                    commStatus = "Full";
                } else if (fastComm === 50) {
                    commStatus = "Half";
                } else if (fastComm === 0) {
                    commStatus = "None";
                }
                return commStatus;
            }
        }
    }

    if (row?.leaderCommissions && Array.isArray(row.leaderCommissions)) {
        for (const item of row.leaderCommissions) {
            if (item?.user?._id === userId) {
                const fastComm = item.leaderReferralCommStatus?.fastComm;
                if (fastComm === 100) {
                    commStatus = "Full";
                } else if (fastComm === 50) {
                    commStatus = "Half";
                } else if (fastComm === 0) {
                    commStatus = "None";
                }
                return commStatus;
            }
        }
    }

    return commStatus;
};

const isLeaderUplineFastCommSelectable = (row, userId) => {
    const commStatus = getLeaderUplineCommStatusLabel(row, userId);
    return (
        commStatus === "-" &&
        row.status === "pending" &&
        moment(row.bookingDate).isAfter("2025-03-16") &&
        moment(row.createdAt).isBefore(sevenDaysLater)
    );
};

const formFields = [
    {
        name: "spaPrice",
        label: "SPA Price",
        type: "numbers",
    },
    {
        name: "rebate",
        label: "Rebate",
        type: "numbers",
    },
    {
        name: "fees",
        label: "Fees",
        type: "numbers",
    },
    {
        name: "cashback",
        label: "Cash Back",
        type: "numbers",
    },
    {
        name: "netPrice",
        label: "Net Price",
        type: "numbers",
    },
    {
        name: "status",
        label: "Status",
    },
    {
        name: "bookingDate",
        label: "Booking Date",
        type: "date",
    },
    {
        name: "unitNumber",
        label: "Unit Number",
    },
    {
        name: "purchaserName",
        label: "Purchaser Name",
    },
    {
        name: "purchaserName2",
        label: "Purchaser Name 2",
    },
    {
        name: "purchaserName3",
        label: "Purchaser Name 3",
    },
    {
        name: "purchaserName4",
        label: "Purchaser Name 4",
    },
    {
        name: "purchaserName5",
        label: "Purchaser Name 5",
    },
];

const commissionFields = [
    {
        name: "agentCommission",
        label: "Agent Commission",
        type: "numbers",
    },
    {
        name: "commissionPercentage",
        label: "Commission Percentage",
        type: "numbers",
    },
    {
        name: "leaderCommission",
        label: "Leader Commission",
        type: "numbers",
    },
    {
        name: "leadercommissionPercentage",
        label: "Leader Commission Percentage",
        type: "numbers",
    },
    {
        name: "primeCommission",
        label: "Prime Leader Commission",
        type: "numbers",
    },
    {
        name: "primeleadercommissionPercentage",
        label: "Prime Commission Percentage",
        type: "numbers",
    },
];

const statusOptions = [
    {
        label: "All",
        value: "all",
    },
    {
        label: "Pending Loan",
        value: "pending",
    },
    {
        label: "Cancelled",
        value: "cancel",
    },
    {
        label: "Converted",
        value: "converted",
    },
    {
        label: "SignedLo",
        value: "signedLo",
    },
];

const FastCases = ({ user, location }) => {
    let renderList = [];
    let uplineRenderList = [];
    const [modalData, setModalData] = useState({});
    const [checklist, setChecklist] = useState([]);
    const [uplinechecklist, setUplineChecklist] = useState([]);
    const [filteredCases, setFilteredCases] = useState([]);

    const selectableAgentCases = useMemo(
        () =>
            filteredCases.filter((row) =>
                isAgentFastCommSelectable(row, user.auth.userId)
            ),
        [filteredCases, user.auth.userId]
    );

    const selectableUplineCases = useMemo(
        () =>
            filteredCases.filter((row) =>
                isLeaderUplineFastCommSelectable(row, user.auth.userId)
            ),
        [filteredCases, user.auth.userId]
    );

    useEffect(() => {
        setChecklist((prev) =>
            prev.filter((caseId) =>
                selectableAgentCases.some((row) => row._id === caseId)
            )
        );
    }, [selectableAgentCases]);

    useEffect(() => {
        setUplineChecklist((prev) =>
            prev.filter((caseId) =>
                selectableUplineCases.some((row) => row._id === caseId)
            )
        );
    }, [selectableUplineCases]);
    const [modalState, setModalState] = useState();
    const [changeCommissionStatusModal, setChangeCommissionStatusModal] =
        useState(false);
    const [
        agentBulkChangeCommissionStatusModal,
        setAgentBulkChangeCommissionStatusModal,
    ] = useState(false);
    const [
        bulkChangeCommissionStatusModal,
        setBulkChangeCommissionStatusModal,
    ] = useState(false);
    const [
        changeAgentCommissionStatusModal,
        setChangeAgentCommissionStatusModal,
    ] = useState(false);
    const [typeCommissionStatus, setTypeCommissionStatus] = useState({});
    const [typeReferralCommissionStatus, setTypeReferralCommissionStatus] =
        useState(undefined);

    const [commissionState, setCommissionState] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [showDetails, setShowDetails] = useState(false);

    const size = useDimensions();

    const { errors } = useForm({
        defaultValues: {
            gallery: [{}],
            salesKit: "",
            spaPrice: "",
            rebate: "",
            fees: "",
            cashback: "",
            netPrice: "",
            status: "",
            bookingDate: moment(Date.now()).format("YYYY-MM-DD"),
            unitNumber: "",
            purchaserName: "",
            purchaserName2: "",
            purchaserName3: "",
            purchaserName4: "",
            purchaserName5: "",
            purchaserIc: "",
            purchaserIc2: "",
            purchaserIc3: "",
            purchaserIc4: "",
            purchaserIc5: "",
            purchaserContact: "",
            purchaserContact2: "",
            purchaserContact3: "",
            purchaserContact4: "",
            purchaserContact5: "",
            project: location.state
                ? location.state.project
                    ? location.state.project
                    : undefined
                : undefined,
        },
    });

    const resetModalData = () => {
        setModalState(false);
    };

    const resetCommissionState = () => {
        setCommissionState(false);
    };
    const resetCommissionStatusModal = () => {
        setChangeCommissionStatusModal(false);
        resetStatusCommissionForm();
    };

    const resetBulkCommissionStatusModal = () => {
        setAgentBulkChangeCommissionStatusModal(false);
        resetStatusCommissionForm();
    };

    const resetBulkUplineCommissionStatusModal = () => {
        setBulkChangeCommissionStatusModal(false);
        resetStatusCommissionForm();
    };

    const resetAgentCommissionStatusModal = () => {
        setChangeAgentCommissionStatusModal(false);
        resetStatusCommissionForm();
    };

    const {
        register: registerStatusForm,
        handleSubmit: handleSubmitStatusForm,
        getValues: getValuesStatusCommissionForm,
        reset: resetStatusCommissionForm,
    } = useForm({
        defaultValues: {
            fastComm: undefined,
        },
    });

    const [editCommissionStatus] = useMutation(queries.EDIT_COMMISSION_STATUS);

    const handleEditStatus = async () => {
        try {
            const formValues = getValuesStatusCommissionForm();

            const result = await editCommissionStatus({
                variables: {
                    memberId: modalData ? modalData.memberId : undefined,
                    caseId: modalData
                        ? modalData.nested
                            ? modalData.nested._id
                            : undefined
                        : undefined,
                    fastComm: formValues.fastComm
                        ? parseFloat(formValues.fastComm)
                        : undefined,
                    type: typeCommissionStatus,
                },
            });

            if (result.data) {
                alert("Success");
                
                // Update the filteredCases state with new commission data
                const updatedCase = result.data.editCommissionStatus;
                setFilteredCases((prevCases) => 
                    prevCases.map(caseItem => 
                        caseItem._id === updatedCase._id 
                            ? {
                                ...caseItem,
                                members: updatedCase.members,
                                sponsorCommissions: updatedCase.sponsorCommissions,
                                leaderCommissions: updatedCase.leaderCommissions,
                                ptp1Commissions: updatedCase.ptp1Commissions,
                                ptp2Commissions: updatedCase.ptp2Commissions,
                                ptpCommissions: updatedCase.ptpCommissions,
                            }
                            : caseItem
                    )
                );

                setModalData((prev) => ({
                    ...prev,
                    members: result.data.editCommissionStatus.members,
                    sponsorCommissions:
                        result.data.editCommissionStatus.sponsorCommissions,
                    leaderCommissions:
                        result.data.editCommissionStatus.leaderCommissions,
                    ptp1Commissions:
                        result.data.editCommissionStatus.ptp1Commissions,
                    ptp2Commissions:
                        result.data.editCommissionStatus.ptp2Commissions,
                    ptpCommissions:
                        result.data.editCommissionStatus.ptpCommissions,
                }));

                if (typeCommissionStatus === "member") {
                    const caseId = updatedCase._id;
                    setChecklist((prev) => prev.filter((id) => id !== caseId));
                    renderList = renderList.filter((id) => id !== caseId);
                }

                setChangeCommissionStatusModal(false);
                setChangeAgentCommissionStatusModal(false);
            }
        } catch (err) {
            alert("Failed");
            console.log(err);
        }
    };

    const [editBulkCommissionStatus] = useMutation(
        queries.EDIT_BULK_COMMISSION_STATUS
    );

    const handleEditBulkStatus = async () => {
        try {
            const formValues = getValuesStatusCommissionForm();
            const eligibleCaseIds = new Set(
                selectableAgentCases.map((row) => row._id)
            );
            const casesToUpdate = checklist.filter((caseId) =>
                eligibleCaseIds.has(caseId)
            );

            if (casesToUpdate.length === 0) {
                alert("No eligible agent commission cases selected");
                return;
            }

            const result = await editBulkCommissionStatus({
                variables: {
                    memberId: modalData ? modalData.memberId : undefined,
                    cases: casesToUpdate,
                    fastComm: formValues.fastComm
                        ? parseFloat(formValues.fastComm)
                        : undefined,
                    type: typeCommissionStatus,
                },
            });

            if (result) {
                alert("Success");
                window.location.reload();
            }
        } catch (err) {
            alert("Failed");
            console.log(err);
        }
    };

    const handleEditLeaderStatus = async () => {
        try {
            const formValues = getValuesStatusCommissionForm();

            const result = await editCommissionStatus({
                variables: {
                    memberId: modalData ? modalData.memberId : undefined,
                    caseId: modalData
                        ? modalData.nested
                            ? modalData.nested._id
                            : undefined
                        : undefined,
                    fastComm: formValues.fastComm
                        ? parseFloat(formValues.fastComm)
                        : undefined,
                    type: typeReferralCommissionStatus,
                },
            });

            if (result.data) {
                alert("Success");
                
                // Update the filteredCases state with new commission data
                const updatedCase = result.data.editCommissionStatus;
                setFilteredCases((prevCases) => 
                    prevCases.map(caseItem => 
                        caseItem._id === updatedCase._id 
                            ? {
                                ...caseItem,
                                members: updatedCase.members,
                                sponsorCommissions: updatedCase.sponsorCommissions,
                                leaderCommissions: updatedCase.leaderCommissions,
                                ptp1Commissions: updatedCase.ptp1Commissions,
                                ptp2Commissions: updatedCase.ptp2Commissions,
                                ptpCommissions: updatedCase.ptpCommissions,
                            }
                            : caseItem
                    )
                );

                setModalData((prev) => ({
                    ...prev,
                    members: result.data.editCommissionStatus.members,
                    sponsorCommissions:
                        result.data.editCommissionStatus.sponsorCommissions,
                    leaderCommissions:
                        result.data.editCommissionStatus.leaderCommissions,
                    ptp1Commissions:
                        result.data.editCommissionStatus.ptp1Commissions,
                    ptp2Commissions:
                        result.data.editCommissionStatus.ptp2Commissions,
                    ptpCommissions:
                        result.data.editCommissionStatus.ptpCommissions,
                }));

                const caseId = updatedCase._id;
                setUplineChecklist((prev) =>
                    prev.filter((id) => id !== caseId)
                );
                uplineRenderList = uplineRenderList.filter(
                    (id) => id !== caseId
                );

                setChangeCommissionStatusModal(false);
            }
        } catch (err) {
            alert("Failed");
            console.log(err);
        }
    };

    const [editBulkUplineCommissionStatus] = useMutation(
        queries.EDIT_BULK_UPLINE_COMMISSION_STATUS
    );

    const handleEditBulkUplineStatus = async () => {
        try {
            const formValues = getValuesStatusCommissionForm();
            const eligibleCaseIds = new Set(
                selectableUplineCases.map((row) => row._id)
            );
            const casesToUpdate = uplinechecklist.filter((caseId) =>
                eligibleCaseIds.has(caseId)
            );

            if (casesToUpdate.length === 0) {
                alert("No eligible overriding commission cases selected");
                return;
            }

            const result = await editBulkUplineCommissionStatus({
                variables: {
                    memberId: modalData ? modalData.memberId : undefined,
                    cases: casesToUpdate,
                    fastComm: formValues.fastComm
                        ? parseFloat(formValues.fastComm)
                        : undefined,
                },
            });

            if (result) {
                alert("Success");
                window.location.reload();
            }
        } catch (err) {
            alert("Failed");
            console.log(err);
        }
    };

    const [paginationState, setPaginationState] = useState({
        limit: 9999,
        page: 1,
    });

    /** Single border on control only — avoid `cases-filter-select` on Select (that class borders the outer container and doubles the border). */
    const casesDetailsFilterSelectStyles = useMemo(
        () => ({
            container: (base) => ({
                ...base,
                border: "none",
                padding: 0,
                background: "transparent",
            }),
            control: (base, state) => ({
                ...base,
                minWidth: "180px",
                borderRadius: "8px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderColor: "rgba(0, 0, 0, 0.1)",
                backgroundColor: "#fff",
                boxShadow: "none",
                outline: "none",
                ...(state.isFocused && {
                    borderColor: "rgba(0, 0, 0, 0.1)",
                    boxShadow: "none",
                }),
            }),
            valueContainer: (base) => ({
                ...base,
                padding: "0.35rem 0.5rem",
            }),
            indicatorsContainer: (base) => ({
                ...base,
                paddingRight: "0.35rem",
            }),
        }),
        []
    );

    const [filters, setFilters] = useState({
        project: location.state
            ? location.state.project
                ? location.state.project
                : null
            : null,
        dateTo: moment(Date.now()).format("YYYY-MM-DD"),
        dateFrom: moment(Date.now()).subtract(1, "months").format("YYYY-MM-DD"),
        downline: null,
    });

    const { data: projectsData } = useQuery(queries.FETCH_PROJECTS, {
        fetchPolicy: "network-only",
        variables: {
            limit: 9999,
            page: 1,
        },
        onError: ({ graphQLErrors, networkError }) => {
            if (graphQLErrors) {
                for (const graphQLError of graphQLErrors) {
                    console.log(graphQLError);
                }
            }

            if (networkError) {
                console.log(networkError);
            }
        },
    });

    const { data: casesData, loading: casesLoading } = useQuery(
        queries.FETCH_FAST_CASES_REPORT,
        {
            fetchPolicy: "network-only",
            variables: {
                limit: paginationState.limit,
                page: paginationState.page,
                by: paginationState.by ? paginationState.by : undefined,
                dir: paginationState.dir ? paginationState.dir : undefined,
                project: filters.project ? filters.project.value : undefined,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                status: filters.status ? filters.status.value : "pending",
                downline: filters.downline ? filters.downline.id : undefined,
            },
            onCompleted: ({ fetchFastCasesAgent: { pagination } }) => {
                setIsLoading(false);
                setPaginationState(pagination);
                
                // Helper function to check if a case can be selected by either checkbox
                const canCaseBeSelected = (row) => {
                    // Check agent checkbox conditions
                    let agentCommStatus = "";
                    if (row.members && Array.isArray(row.members)) {
                        row.members.forEach((item) => {
                            if (item && item.member && item.member._id === user.auth.userId) {
                                if (item.fullClaimComm != null) {
                                    agentCommStatus = "Full Claim";
                                } else if (item.secondBatchComm != null) {
                                    agentCommStatus = "Second Batch";
                                } else if (item.firstBatchComm != null) {
                                    agentCommStatus = "First Batch";
                                } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 100) {
                                    agentCommStatus = "Full";
                                } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 50) {
                                    agentCommStatus = "Half";
                                } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 0) {
                                    agentCommStatus = "None";
                                } else {
                                    agentCommStatus = "-";
                                }
                                return;
                            }
                        });
                    } else {
                        agentCommStatus = "-";
                    }

                    // Check upline checkbox conditions
                    let uplineCommStatus = "";
                    if (row.sponsorCommissions && Array.isArray(row.sponsorCommissions)) {
                        row.sponsorCommissions.forEach((item) => {
                            if (item && item.user && item.user._id === user.auth.userId) {
                                if (item.referralCommStatus && item.referralCommStatus.fastComm === 100) {
                                    uplineCommStatus = "FastComm-Full";
                                } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 50) {
                                    uplineCommStatus = "FastComm-Half";
                                } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 0) {
                                    uplineCommStatus = "None";
                                } else {
                                    uplineCommStatus = "-";
                                }
                                return;
                            }
                        });
                    }

                    if (row.leaderCommissions && Array.isArray(row.leaderCommissions)) {
                        row.leaderCommissions.forEach((item) => {
                            if (item && item.user && item.user._id === user.auth.userId) {
                                if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 100) {
                                    uplineCommStatus = "FastComm-Full";
                                } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 50) {
                                    uplineCommStatus = "FastComm-Half";
                                } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 0) {
                                    uplineCommStatus = "None";
                                } else {
                                    uplineCommStatus = "-";
                                }
                                return;
                            }
                        });
                    }

                    // Agent checkbox condition: commStatus === "-" && status === "pending" && booking date after "2025-03-16" && created before sevenDaysLater
                    const canSelectAsAgent = (agentCommStatus === "-" && row.status === "pending" &&
                        moment(row.bookingDate).isAfter("2025-03-16") &&
                        moment(row.createdAt).isBefore(sevenDaysLater));

                    // Upline checkbox condition: commStatus === "-" && booking date after "2025-03-16" && created before sevenDaysLater && (status === "converted" || "pending")
                    const canSelectAsUpline = (uplineCommStatus === "-" && moment(row.bookingDate).isAfter("2025-03-16") &&
                        moment(row.createdAt).isBefore(sevenDaysLater) &&
                        (row.status === "pending"));

                    return canSelectAsAgent || canSelectAsUpline;
                };

                // Filter main data to only include cases that can be selected by either checkbox
                const selectableCases = casesData.fetchFastCasesAgent.data.filter(canCaseBeSelected);
                
                // Set the filtered cases to state so it can be used in the table
                setFilteredCases(selectableCases);
            },
            
        }
    );

    // const { data: downlinesData } = useQuery(queries.FETCH_DOWNLINES, {
    //     variables: {
    //         limit: paginationState.limit,
    //         page: paginationState.page,
    //         name: filters.name,
    //         referral: filters.referral,
    //     },
    //     fetchPolicy: "network-only",
    //     onCompleted: ({ fetchDownlines: { pagination } }) =>
    //         setPaginationState(pagination),
    // });

    const columns = useMemo(() => {
        const NumberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        if (size.width > 650) {
            return [
                {
                    Header: () => {
                        const selectableCount = selectableAgentCases.length;
                        const isAllSelected =
                            selectableCount > 0 &&
                            checklist.length === selectableCount;

                        return (
                            <div className="flex justify-center items-center">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const allIds = selectableAgentCases.map(
                                                (row) => row._id
                                            );
                                            setChecklist(allIds);
                                            //eslint-disable-next-line
                                            renderList = allIds;
                                        } else {
                                            setChecklist([]);
                                            renderList = [];
                                        }
                                    }}
                                />
                            </div>
                        );
                    },
                    accessor: "_id",
                    Cell: ({ value, row: { original } }) => {
                        let commStatus = "";
                        if (original.members && Array.isArray(original.members)) {
                            original.members.forEach((item) => {
                                if (item && item.member && item.member._id === user.auth.userId) {
                                    if (item.fullClaimComm != null) {
                                        commStatus = "Full Claim";
                                    } else if (item.secondBatchComm != null) {
                                        commStatus = "Second Batch";
                                    } else if (item.firstBatchComm != null) {
                                        commStatus = "First Batch";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 100) {
                                        commStatus = "Full";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 50) {
                                        commStatus = "Half";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    return;
                                }
                            });
                        } else {
                            commStatus = "-";
                        }

                        return (
                            isAgentFastCommSelectable(
                                original,
                                user.auth.userId
                            ) && (
                                <div className="flex justify-center items-center">
                                    <input
                                        type="checkbox"
                                        checked={checklist.includes(value)}
                                        onChange={() => {
                                            if (value) {
                                                setChecklist(
                                                    (prevChecklist) => {
                                                        const exists =
                                                            prevChecklist.some(
                                                                (item) =>
                                                                    item ===
                                                                    value
                                                            );

                                                        if (exists) {
                                                            return prevChecklist.filter(
                                                                (item) =>
                                                                    item !==
                                                                    value
                                                            );
                                                        } else {
                                                            return [
                                                                ...prevChecklist,
                                                                value,
                                                            ];
                                                        }
                                                    }
                                                );

                                                if (
                                                    renderList.some(
                                                        (item) => item === value
                                                    )
                                                ) {
                                                    // Remove the item if it's already in the checklist
                                                    //eslint-disable-next-line
                                                    renderList =
                                                        renderList.filter(
                                                            (item) =>
                                                                item !== value
                                                        );
                                                } else {
                                                    // Add the item if it's not in the checklist
                                                    renderList.push(value);
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            )
                        );
                    },
                },
                {
                    Header: "Project Name",
                    accessor: "project",
                    Cell: ({ value }) => {
                        if (value) {
                            return <div> {value.name}</div>;
                        }

                        return <div></div>;
                    },
                },
                {
                    Header: "Unit No.",
                    accessor: "unitNumber",
                },
              

                {
                    Header: "Agent Comm. Status",
                    id: "commStatus",
                    Cell: ({ row: { original } }) => {
                        let commStatus = "";
                        let memberId = undefined;
                        if (original.members && Array.isArray(original.members)) {
                            original.members.forEach((item) => {
                                if (item && item.member && item.member._id === user.auth.userId) {
                                    if (item.fullClaimComm != null) {
                                        commStatus = "Full Claim";
                                    } else if (item.secondBatchComm != null) {
                                        commStatus = "Second Batch";
                                    } else if (item.firstBatchComm != null) {
                                        commStatus = "First Batch";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 100) {
                                        commStatus = "Full";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 50) {
                                        commStatus = "Half";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                    return;
                                }
                            });
                        } else {
                            commStatus = "-";
                        }

                        return (
                            <div className="flex items-start">
                                <div>{commStatus}</div>
                                {
                                    ((commStatus === "-" && original.status === "pending" &&
                                        moment(original.bookingDate).isAfter(
                                            "2025-03-16"
                                        ) &&
                                        moment(original.createdAt).isBefore(
                                            sevenDaysLater
                                        ))) && (
                                        <div className="cursor-pointer ml-2">
                                            <FiEdit
                                                onClick={() => {
                                                    setTypeReferralCommissionStatus(
                                                        undefined
                                                    );

                                                    if (original.members && Array.isArray(original.members)) {
                                                        original.members.forEach(
                                                            (item) => {
                                                                if (
                                                                    item && item.member &&
                                                                    item.member._id ===
                                                                    user.auth.userId
                                                                ) {
                                                                    setTypeCommissionStatus(
                                                                        "member"
                                                                    );
                                                                    return;
                                                                }
                                                            }
                                                        );
                                                    }

                                                    setModalData({
                                                        ...modalData,
                                                        nested: original,
                                                        memberId: memberId,
                                                    });

                                                    setChangeAgentCommissionStatusModal(
                                                        true
                                                    );
                                                }}
                                            ></FiEdit>
                                        </div>
                                    )}
                            </div>
                        );
                    },
                },

                {
                    Header: "Leader / Prime Comm. Status",
                    id: "referralcommStatus",
                    Cell: ({ row: { original } }) => {
                        let commStatus = "";
                        let memberId = undefined;

                        if (original.sponsorCommissions && Array.isArray(original.sponsorCommissions)) {
                            original.sponsorCommissions.forEach((item) => {
                                if (item && item.user && item.user._id === user.auth.userId) {
                                    if (item.referralCommStatus && item.referralCommStatus.fastComm === 100) {
                                        commStatus = "Full";
                                    } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 50) {
                                        commStatus = "Half";
                                    } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                    return;
                                }
                            });
                        }

                        if (original.leaderCommissions && Array.isArray(original.leaderCommissions)) {
                            original.leaderCommissions.forEach((item) => {
                                if (item && item.user && item.user._id === user.auth.userId) {
                                    if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 100) {
                                        commStatus = "Full";
                                    } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 50) {
                                        commStatus = "Half";
                                    } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                    return;
                                }
                            });
                        }

                        return (
                            <div className="flex items-start">
                                <div>{commStatus}</div>
                                {
                                    ((commStatus === "-" && moment(original.bookingDate).isAfter(
                                        "2025-03-16"
                                    ) &&
                                        moment(original.createdAt).isBefore(
                                            sevenDaysLater
                                        ) &&
                                        (
                                            original.status === "pending"))) && (
                                        <div className="cursor-pointer ml-2">
                                            <FiEdit
                                                onClick={() => {
                                                    if (original.sponsorCommissions && Array.isArray(original.sponsorCommissions)) {
                                                        original.sponsorCommissions.forEach(
                                                            (item) => {
                                                                if (
                                                                    item && item.user &&
                                                                    item.user._id ===
                                                                    user.auth.userId
                                                                ) {
                                                                    setTypeReferralCommissionStatus(
                                                                        "referral"
                                                                    );
                                                                    return;
                                                                }
                                                            }
                                                        );
                                                    }

                                                    const isUserInSponsorCommissions =
                                                        original.sponsorCommissions && Array.isArray(original.sponsorCommissions) &&
                                                        original.sponsorCommissions.some(
                                                            (item) =>
                                                                item && item.user &&
                                                                item.user._id ===
                                                                user.auth.userId
                                                        );

                                                    if (original.leaderCommissions && Array.isArray(original.leaderCommissions)) {
                                                        original.leaderCommissions.forEach(
                                                            (item) => {
                                                                if (
                                                                    item && item.user &&
                                                                    item.user._id ===
                                                                    user.auth.userId
                                                                ) {
                                                                    setTypeReferralCommissionStatus(
                                                                        isUserInSponsorCommissions
                                                                            ? "memberreferralleader"
                                                                            : "leaderReferral"
                                                                    );
                                                                    return;
                                                                }
                                                            }
                                                        );
                                                    }

                                                    setModalData({
                                                        ...modalData,
                                                        nested: original,
                                                        memberId: memberId,
                                                    });

                                                    setChangeCommissionStatusModal(
                                                        true
                                                    );
                                                }}
                                            ></FiEdit>
                                        </div>
                                    )}
                            </div>
                        );
                    },
                },
                {
                    Header: () => {
                        const selectableCount = selectableUplineCases.length;
                        const isAllSelected =
                            selectableCount > 0 &&
                            uplinechecklist.length === selectableCount;

                        return (
                            <div className="flex justify-center items-center">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const allIds = selectableUplineCases.map(
                                                (row) => row._id
                                            );
                                            setUplineChecklist(allIds);
                                            //eslint-disable-next-line
                                            uplineRenderList = allIds;
                                        } else {
                                            setUplineChecklist([]);
                                            uplineRenderList = [];
                                        }
                                    }}
                                />
                            </div>
                        );
                    },
                    accessor: "uplineFastComm",
                    Cell: ({ row: { original } }) => {
                        const value = original._id;
                        let commStatus = "";
                        let memberId = "";
                        if (original.sponsorCommissions && Array.isArray(original.sponsorCommissions)) {
                            original.sponsorCommissions.forEach((item) => {
                                if (item && item.user && item.user._id === user.auth.userId) {
                                    if (item.referralCommStatus && item.referralCommStatus.fastComm === 100) {
                                        commStatus = "FastComm-Full";
                                    } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 50) {
                                        commStatus = "FastComm-Half";
                                    } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                    return;
                                }
                            });
                        }

                        if (original.leaderCommissions && Array.isArray(original.leaderCommissions)) {
                            original.leaderCommissions.forEach((item) => {
                                if (item && item.user && item.user._id === user.auth.userId) {
                                    if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 100) {
                                        commStatus = "FastComm-Full";
                                    } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 50) {
                                        commStatus = "FastComm-Half";
                                    } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                    return;
                                }
                            });
                        }

                        return (
                            isLeaderUplineFastCommSelectable(
                                original,
                                user.auth.userId
                            ) && (
                                <div className="flex justify-center items-center">
                                    <input
                                        type="checkbox"
                                        checked={uplinechecklist.includes(value)}
                                        onChange={() => {
                                            if (value) {
                                                setUplineChecklist(
                                                    (prevChecklist) => {
                                                        const exists =
                                                            prevChecklist.some(
                                                                (item) =>
                                                                    item ===
                                                                    value
                                                            );

                                                        if (exists) {
                                                            return prevChecklist.filter(
                                                                (item) =>
                                                                    item !==
                                                                    value
                                                            );
                                                        } else {
                                                            return [
                                                                ...prevChecklist,
                                                                value,
                                                            ];
                                                        }
                                                    }
                                                );

                                                if (
                                                    uplineRenderList.some(
                                                        (item) => item === value
                                                    )
                                                ) {
                                                    // Remove the item if it's already in the checklist
                                                    //eslint-disable-next-line
                                                    uplineRenderList =
                                                    uplineRenderList.filter(
                                                            (item) =>
                                                                item !== value
                                                        );
                                                } else {
                                                    // Add the item if it's not in the checklist
                                                    uplineRenderList.push(value);
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            )
                        );
                    },
                },
                {
                    Header: "Net Price",
                    accessor: "netPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Status",
                    accessor: "status",
                    Cell: ({ value }) => (
                        <div className="capitalize">{value}</div>
                    ),
                },
                {
                    Header: "Booking Date",
                    accessor: "bookingDate",
                },
                {
                    Header: "Converted Date",
                    accessor: "convertDate",
                    Cell: ({ value }) => (
                        <div className="capitalize">
                            {value ? moment(value).format("YYYY-MM-DD") : "-"}
                        </div>
                    ),
                },
                {
                    Header: "Involved Agent",
                    accessor: "worker",
                    Cell: ({ value }) => (
                        <div className="capitalize">{value}</div>
                    ),
                },

                {
                    Header: "",
                    id: "actions",
                    Cell: ({ row: { original } }) => {
                        return (
                            <div className="flex justify-center items-center cursor-pointer">
                                <div
                                    className="mr-2"
                                    onClick={() => {
                                        setModalState(true);
                                        setModalData({
                                            ...original,
                                        });
                                    }}
                                >
                                    <FiFileText />
                                </div>

                                <div
                                    onClick={() => {
                                        setCommissionState(true);
                                        let totalAgentComm = 0;
                                        let totalLeaderComm = 0;
                                        let leadercommissionPercentage = 0;
                                        let totalSponsorComm = 0;
                                        let primeleadercommissionPercentage = 0;
                                        let commissionPercentage = 0;

                                        if (original.members && Array.isArray(original.members)) {
                                            original.members.forEach((item) => {
                                                if (
                                                    item && item.member &&
                                                    item.member._id ===
                                                    user.auth.userId
                                                ) {
                                                    const agentComm =
                                                        item.newCommission
                                                            ? (item.newCommission *
                                                                  original.netPrice) /
                                                              100 /
                                                              1.08
                                                            : (item.commission *
                                                                  original.netPrice) /
                                                              100 /
                                                              1.08;
                                                    totalAgentComm =
                                                        totalAgentComm + agentComm;
                                                    const commission =
                                                        item.newCommission
                                                            ? item.newCommission
                                                            : item.commission;
                                                    commissionPercentage =
                                                        commissionPercentage +
                                                        commission;
                                                    return;
                                                }
                                            });
                                        }

                                        if (original.leaderCommissions && Array.isArray(original.leaderCommissions)) {
                                            original.leaderCommissions.forEach(
                                                (item, i) => {
                                                    if (
                                                        item && item.user &&
                                                        item.user._id ===
                                                        user.auth.userId
                                                    ) {
                                                        const leaderComm =
                                                            (item.commission *
                                                                original.netPrice) /
                                                            100 /
                                                            1.08;
                                                        totalLeaderComm =
                                                            totalLeaderComm +
                                                            leaderComm;
                                                        primeleadercommissionPercentage =
                                                            item.newCommission
                                                                ? item.newCommission
                                                                : item.commission;
                                                    }
                                                }
                                            );
                                        }

                                        if (original.sponsorCommissions && Array.isArray(original.sponsorCommissions)) {
                                            original.sponsorCommissions.forEach(
                                                (item, i) => {
                                                    if (
                                                        item && item.user &&
                                                        item.user._id ===
                                                        user.auth.userId
                                                    ) {
                                                        const sponsorComm =
                                                            (item.commission *
                                                                original.netPrice) /
                                                            100 /
                                                            1.08;
                                                        totalSponsorComm =
                                                            totalSponsorComm +
                                                            sponsorComm;
                                                        leadercommissionPercentage =
                                                            item.newCommission
                                                                ? item.newCommission
                                                                : item.commission;
                                                    }
                                                }
                                            );
                                        }
                                        setModalData({
                                            ...original,
                                            agentCommission: totalAgentComm,
                                            leaderCommission: totalSponsorComm,
                                            primeCommission: totalLeaderComm,
                                            commissionPercentage:
                                                commissionPercentage,
                                            leadercommissionPercentage:
                                                leadercommissionPercentage,
                                            primeleadercommissionPercentage:
                                                primeleadercommissionPercentage,
                                        });
                                    }}
                                >
                                    <FiMonitor />
                                </div>
                            </div>
                        );
                    },
                },
            ];
        } else {
            return [
                {
                    Header: () => {
                        const selectableCount = selectableAgentCases.length;
                        const isAllSelected =
                            selectableCount > 0 &&
                            checklist.length === selectableCount;

                        return (
                            <div className="flex justify-center items-center">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const allIds = selectableAgentCases.map(
                                                (row) => row._id
                                            );
                                            setChecklist(allIds);
                                            //eslint-disable-next-line
                                            renderList = allIds;
                                        } else {
                                            setChecklist([]);
                                            renderList = [];
                                        }
                                    }}
                                />
                            </div>
                        );
                    },
                    accessor: "_id",
                    Cell: ({ value, row: { original } }) => {
                        let commStatus = "";
                        if (original.members && Array.isArray(original.members)) {
                            original.members.forEach((item) => {
                                if (item && item.member && item.member._id === user.auth.userId) {
                                    if (item.fullClaimComm != null) {
                                        commStatus = "Full Claim";
                                    } else if (item.secondBatchComm != null) {
                                        commStatus = "Second Batch";
                                    } else if (item.firstBatchComm != null) {
                                        commStatus = "First Batch";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 100) {
                                        commStatus = "Full";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 50) {
                                        commStatus = "Half";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    return;
                                }
                            });
                        } else {
                            commStatus = "-";
                        }

                        return (
                            isAgentFastCommSelectable(
                                original,
                                user.auth.userId
                            ) && (
                                <div className="flex justify-center items-center">
                                    <input
                                        type="checkbox"
                                        checked={checklist.includes(value)}
                                        onChange={() => {
                                            if (value) {
                                                setChecklist(
                                                    (prevChecklist) => {
                                                        const exists =
                                                            prevChecklist.some(
                                                                (item) =>
                                                                    item ===
                                                                    value
                                                            );

                                                        if (exists) {
                                                            return prevChecklist.filter(
                                                                (item) =>
                                                                    item !==
                                                                    value
                                                            );
                                                        } else {
                                                            return [
                                                                ...prevChecklist,
                                                                value,
                                                            ];
                                                        }
                                                    }
                                                );

                                                if (
                                                    renderList.some(
                                                        (item) => item === value
                                                    )
                                                ) {
                                                    // Remove the item if it's already in the checklist
                                                    //eslint-disable-next-line
                                                    renderList =
                                                        renderList.filter(
                                                            (item) =>
                                                                item !== value
                                                        );
                                                } else {
                                                    // Add the item if it's not in the checklist
                                                    renderList.push(value);
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            )
                        );
                    },
                },
                {
                    Header: "Unit Number",
                    id: "unitNumber",
                    Cell: ({ row: { original } }) => {
                        if (original) {
                            return (
                                <div className="capitalize">
                                    {" "}
                                    {original.unitNumber}
                                </div>
                            );
                        }

                        return <div></div>;
                    },
                },
                {
                    Header: "Booking Date",
                    accessor: "bookingDate",
                },
                {
                    Header: "Agent Commission",
                    id: "commission",
                    Cell: ({ row: { original } }) => {
                        let totalAgentComm = 0;
                        if (original.members && Array.isArray(original.members)) {
                            original.members.forEach((item) => {
                                if (item && item.member && item.member._id === user.auth.userId) {
                                    const agentComm = item.newCommission
                                        ? (item.newCommission * original.netPrice) /
                                          100 /
                                          1.08
                                        : (item.commission *
                                              original.netPrice) /
                                          100 /
                                          1.08;
                                    totalAgentComm = totalAgentComm + agentComm;
                                    return;
                                }
                            });
                        }
                        return (
                            <div className="flex justify-center items-center">
                                RM {NumberFormatter.format(totalAgentComm)}
                            </div>
                        );
                    },
                },
                {
                    Header: "Agent Comm",
                    id: "commStatus",
                    Cell: ({ row: { original } }) => {
                        let commStatus = "";
                        let memberId = undefined;
                        if (original.members && Array.isArray(original.members)) {
                            original.members.forEach((item) => {
                                if (item && item.member && item.member._id === user.auth.userId) {
                                    if (item.fullClaimComm != null) {
                                        commStatus = "Full Claim";
                                    } else if (item.secondBatchComm != null) {
                                        commStatus = "Second Batch";
                                    } else if (item.firstBatchComm != null) {
                                        commStatus = "First Batch";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 100) {
                                        commStatus = "Full";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 50) {
                                        commStatus = "Half";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                    return;
                                }
                            });
                        } else {
                            commStatus = "-";
                        }

                        return (
                            <div className="flex items-start">
                                <div>{commStatus}</div>
                                {
                                    ((commStatus === "-" && original.status === "pending" &&
                                        moment(original.bookingDate).isAfter(
                                            "2025-03-16"
                                        ) &&
                                        moment(original.createdAt).isBefore(
                                            sevenDaysLater
                                        ))) && (
                                        <div className="cursor-pointer ml-2">
                                            <FiEdit
                                                onClick={() => {
                                                    setTypeReferralCommissionStatus(
                                                        undefined
                                                    );

                                                    if (original.members && Array.isArray(original.members)) {
                                                    original.members.forEach(
                                                        (item) => {
                                                            if (
                                                                    item && item.member &&
                                                                    item.member._id ===
                                                                user.auth.userId
                                                            ) {
                                                                setTypeCommissionStatus(
                                                                    "member"
                                                                );
                                                                return;
                                                            }
                                                        }
                                                    );
                                                    }

                                                    setModalData({
                                                        ...modalData,
                                                        nested: original,
                                                        memberId: memberId,
                                                    });

                                                    setChangeAgentCommissionStatusModal(
                                                        true
                                                    );
                                                }}
                                            ></FiEdit>
                                        </div>
                                    )}
                            </div>
                        );
                    },
                },

                {
                    Header: "Overriding Comm",
                    id: "referralcommStatus",
                    Cell: ({ row: { original } }) => {
                        let commStatus = "";
                        let memberId = undefined;

                        if (original.sponsorCommissions && Array.isArray(original.sponsorCommissions)) {
                        original.sponsorCommissions.forEach((item) => {
                                if (item && item.user && item.user._id === user.auth.userId) {
                                    if (item.referralCommStatus && item.referralCommStatus.fastComm === 100) {
                                        commStatus = "Full";
                                    } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 50) {
                                        commStatus = "Half";
                                    } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                memberId = item._id;
                                return;
                            }
                        });
                        }

                        if (original.leaderCommissions && Array.isArray(original.leaderCommissions)) {
                        original.leaderCommissions.forEach((item) => {
                                if (item && item.user && item.user._id === user.auth.userId) {
                                    if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 100) {
                                        commStatus = "Full";
                                    } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 50) {
                                        commStatus = "Half";
                                    } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                memberId = item._id;
                                return;
                            }
                        });
                        }

                        return (
                            <div className="flex items-start">
                                <div>{commStatus}</div>
                                {
                                    ((commStatus === "-" && moment(original.bookingDate).isAfter(
                                        "2025-03-16"
                                    ) &&
                                        moment(original.createdAt).isBefore(
                                            sevenDaysLater
                                        ) &&
                                        (
                                            original.status === "pending"))) && (
                                        <div className="cursor-pointer ml-2">
                                            <FiEdit
                                                onClick={() => {
                                                    if (original.sponsorCommissions && Array.isArray(original.sponsorCommissions)) {
                                                    original.sponsorCommissions.forEach(
                                                        (item) => {
                                                            if (
                                                                    item && item.user &&
                                                                    item.user._id ===
                                                                user.auth.userId
                                                            ) {
                                                                setTypeReferralCommissionStatus(
                                                                    "referral"
                                                                );
                                                                return;
                                                            }
                                                        }
                                                    );
                                                    }

                                                    const isUserInSponsorCommissions =
                                                        original.sponsorCommissions && Array.isArray(original.sponsorCommissions) &&
                                                        original.sponsorCommissions.some(
                                                            (item) =>
                                                                item && item.user &&
                                                                item.user._id ===
                                                                user.auth.userId
                                                        );

                                                    if (original.leaderCommissions && Array.isArray(original.leaderCommissions)) {
                                                    original.leaderCommissions.forEach(
                                                        (item) => {
                                                            if (
                                                                    item && item.user &&
                                                                    item.user._id ===
                                                                user.auth.userId
                                                            ) {
                                                                setTypeReferralCommissionStatus(
                                                                    isUserInSponsorCommissions
                                                                        ? "memberreferralleader"
                                                                        : "leaderReferral"
                                                                );
                                                                return;
                                                            }
                                                        }
                                                    );
                                                    }

                                                    setModalData({
                                                        ...modalData,
                                                        nested: original,
                                                        memberId: memberId,
                                                    });

                                                    setChangeCommissionStatusModal(
                                                        true
                                                    );
                                                }}
                                            ></FiEdit>
                                        </div>
                                    )}
                            </div>
                        );
                    },
                },
                {
                    Header: () => {
                        const selectableCount = selectableUplineCases.length;
                        const isAllSelected =
                            selectableCount > 0 &&
                            uplinechecklist.length === selectableCount;

                        return (
                            <div className="flex justify-center items-center">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const allIds = selectableUplineCases.map(
                                                (row) => row._id
                                            );
                                            setUplineChecklist(allIds);
                                            //eslint-disable-next-line
                                            uplineRenderList = allIds;
                                        } else {
                                            setUplineChecklist([]);
                                            uplineRenderList = [];
                                        }
                                    }}
                                />
                            </div>
                        );
                    },
                    accessor: "uplineFastComm",
                    Cell: ({ row: { original } }) => {
                        const value = original._id;
                        let commStatus = "";
                        let memberId = "";
                        if (original.sponsorCommissions && Array.isArray(original.sponsorCommissions)) {
                            original.sponsorCommissions.forEach((item) => {
                                if (item && item.user && item.user._id === user.auth.userId) {
                                    if (item.referralCommStatus && item.referralCommStatus.fastComm === 100) {
                                        commStatus = "FastComm-Full";
                                    } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 50) {
                                        commStatus = "FastComm-Half";
                                    } else if (item.referralCommStatus && item.referralCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                return;
                            }
                        });
                        }

                        if (original.leaderCommissions && Array.isArray(original.leaderCommissions)) {
                            original.leaderCommissions.forEach((item) => {
                                if (item && item.user && item.user._id === user.auth.userId) {
                                    if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 100) {
                                        commStatus = "FastComm-Full";
                                    } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 50) {
                                        commStatus = "FastComm-Half";
                                    } else if (item.leaderReferralCommStatus && item.leaderReferralCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                    return;
                                }
                            });
                        }

                        return (
                            isLeaderUplineFastCommSelectable(
                                original,
                                user.auth.userId
                            ) && (
                                <div className="flex justify-center items-center">
                                    <input
                                        type="checkbox"
                                        checked={uplinechecklist.includes(value)}
                                        onChange={() => {
                                            if (value) {
                                                setUplineChecklist(
                                                    (prevChecklist) => {
                                                        const exists =
                                                            prevChecklist.some(
                                                                (item) =>
                                                                    item ===
                                                                    value
                                                            );

                                                        if (exists) {
                                                            return prevChecklist.filter(
                                                                (item) =>
                                                                    item !==
                                                                    value
                                                            );
                                                        } else {
                                                            return [
                                                                ...prevChecklist,
                                                                value,
                                                            ];
                                                        }
                                                    }
                                                );

                                                if (
                                                    uplineRenderList.some(
                                                        (item) => item === value
                                                    )
                                                ) {
                                                    // Remove the item if it's already in the checklist
                                                    //eslint-disable-next-line
                                                    uplineRenderList =
                                                    uplineRenderList.filter(
                                                            (item) =>
                                                                item !== value
                                                        );
                                                } else {
                                                    // Add the item if it's not in the checklist
                                                    uplineRenderList.push(value);
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            )
                        );
                    },
                },
            ];
        }
    }, [modalData, size.width, setModalData, user.auth.userId, selectableAgentCases, checklist, selectableUplineCases, uplinechecklist]);

    const NumberFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return (
        <>
            {isLoading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                </div>
            ) : (
                <div className="my-10 lg:my-0 lg:p-8">
                    {showDetails ? (
                        // Summary Card
                        <div className="cases-summary-card">
                            <h1 className="cases-title">CASES</h1>
                            <div className="cases-grid">
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Cases</div>
                                    <div className="cases-stat-value">
                                        {casesData?.fetchFastCasesAgent?.totals?.cases || "0"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total SPA Price</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.spaPrice ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.spaPrice) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Net Price</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.netPrice ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.netPrice) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Net GDV</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.totalNetGdv ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.totalNetGdv) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Group Net GDV</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.totalGroupNetGdv ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.totalGroupNetGdv) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Commission Percentage</div>
                                    <div className="cases-stat-value">
                                        {casesData?.fetchFastCasesAgent?.totals?.totalCommissionPercentage || "0"}%
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Personal Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.totalCommission ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.totalCommission) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Leader Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.totalLeaderComm ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.totalLeaderComm) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Prime Leader Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.totalPrimeLeaderComm ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.totalPrimeLeaderComm) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">PTP 5 Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.totalPtpReferralComm ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.totalPtpReferralComm) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">PTP 2 Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.totalPtp2ReferralComm ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.totalPtp2ReferralComm) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">PTP 1 Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchFastCasesAgent?.totals?.totalPtp1ReferralComm ? NumberFormatter.format(casesData.fetchFastCasesAgent.totals.totalPtp1ReferralComm) : "0.00"}
                                    </div>
                                </div>
                            </div>
                            <button 
                                className="cases-view-details"
                                onClick={() => setShowDetails(true)}
                            >
                                VIEW DETAILS
                            </button>
                        </div>
                    ) : (
                        // Details Card
                        <div className="cases-details-card">
                            
                            <div className="cases-filters">
                                        <Select
                                            styles={casesDetailsFilterSelectStyles}
                                            options={
                                                projectsData
                                                    ? projectsData.fetchProjectsAgent.data.map((project) => ({
                                                          label: project.name,
                                                          value: project._id,
                                                      }))
                                                    : []
                                            }
                                            value={filters.project}
                                            placeholder="Project"
                                            onChange={(val) => {
                                                setPaginationState((prev) => ({ ...prev, page: 1 }));
                                                setIsLoading(true);
                                                setFilters((prev) => ({ ...prev, project: val }));
                                            }}
                                        />
                                <button className="cases-search-btn">Search</button>
                                    </div>

                            <div className="cases-table-actions">
                            <button
                                    className="cases-action-btn"
                                onClick={() => {
                                        setAgentBulkChangeCommissionStatusModal(true);
                                    setTypeCommissionStatus("member");
                                }}
                            >
                                Agent Comm
                            </button>
                                <button
                                    className="cases-action-btn"
                                    onClick={() => setBulkChangeCommissionStatusModal(true)}
                                >
                                Overriding Comm
                            </button>
                        </div>

                            <div className="cases-table-container">
                            { selectableAgentCases.length === 0 && selectableUplineCases.length === 0 && !casesLoading ? (
                                    <div className="flex flex-col items-center justify-center py-16 px-4 md:px-0">
                                        <div className="text-gray-800 text-base md:text-xl font-semibold mb-4 text-center">
                                            There is no remaining case to select fast or full comm.
                                        </div>
                                        <button
                                            onClick={() => window.location.href = "/cases"}
                                            className="text-indigo-600 hover:text-indigo-800 underline font-medium cursor-pointer"
                                        >
                                            Click here to see all cases
                                        </button>
                                    </div>
                                ) : (
                        <Table
                            columns={columns}
                                    data={filteredCases}
                            updatePagination={setPaginationState}
                            paginationState={paginationState}
                            loading={casesLoading}
                                    pageCount={casesData ? casesData.fetchFastCasesAgent.pagination.totalPages : 0}
                                    totalCount={filteredCases.length}
                            hiddenFields={
                                user.auth.ranking.name === "agent"
                                    ? ["leaderCommission", "primeCommission"]
                                    : user.auth.ranking.name === "leader"
                                    ? ["primeCommission"]
                                    : undefined
                            }
                        />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* Keep existing modals */}
                        {modalState && (
                            <Modal reset={resetModalData}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container" style={{ maxHeight: '90vh', width: '800px', maxWidth: '95vw' }}>
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                                Case Summary
                                            </h2>
                                        <div className="flex justify-end">
                                            <FiX
                                                onClick={() => {
                                                    setModalState(false);
                                                }}
                                                className="cursor-pointer"
                                            />
                                        </div>
                                        </div>
                                        <div className="commission-modal-body" style={{ maxHeight: 'calc(90vh - 80px)', overflowY: 'auto', paddingRight: '10px' }}>
                                        {formFields.map((item, i) => {
                                            if (
                                                item.name === "purchaserName" ||
                                                item.name === "purchaserIc" ||
                                                item.name === "purchaserContact"
                                            ) {
                                                return (
                                                    <div
                                                        className="flex row"
                                                        key={i}
                                                            style={{ marginBottom: '12px' }}
                                                    >
                                                            <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium whitespace-nowrap pr-4">
                                                            {item.label} :
                                                        </div>
                                                        <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs capitalize">
                                                            {modalData.purchaser
                                                                ? modalData
                                                                      .purchaser[
                                                                      item.name
                                                                  ]
                                                                : ""}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div
                                                    className="flex row"
                                                    key={item.name}
                                                        style={{ marginBottom: '12px' }}
                                                >
                                                        <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium whitespace-nowrap pr-4">
                                                        {item.label} :
                                                    </div>
                                                        <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs capitalize">
                                                        {item.type === "numbers"
                                                            ? `RM ${NumberFormatter.format(
                                                                  modalData[
                                                                      item.name
                                                                  ]
                                                              )}`
                                                            : modalData[
                                                                  item.name
                                                              ]}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                            <div className="flex row" style={{ marginBottom: '12px' }}>
                                                <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium whitespace-nowrap pr-4">
                                                Project :
                                            </div>
                                                <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs">
                                                {modalData.project.name}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {changeCommissionStatusModal && (
                            <Modal reset={resetCommissionStatusModal}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container">
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                            Change Commission Status
                                            </h2>
                                        </div>
                                        <div className="commission-modal-body">
                                        <form
                                            onSubmit={handleSubmitStatusForm(
                                                typeReferralCommissionStatus
                                                    ? handleEditLeaderStatus
                                                    : handleEditStatus
                                            )}
                                        >
                                            <div>
                                                    <div className="commission-modal-label">
                                                        Fast Commission Status
                                                </div>
                                                <select
                                                        className="commission-modal-select"
                                                    name="fastComm"
                                                    ref={registerStatusForm({
                                                        required: false,
                                                    })}
                                                    placeholder="Status"
                                                    type="string"
                                                >
                                                    <option value="0">
                                                        None (0%)
                                                    </option>
                                                    <option value="100">
                                                        Full Comm (100%)
                                                    </option>
                                                </select>
                                            </div>
                                                <button className="commission-modal-button">
                                                Submit
                                            </button>
                                        </form>
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {agentBulkChangeCommissionStatusModal && (
                            <Modal reset={resetBulkCommissionStatusModal}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container">
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                                Bulk Agent Commission
                                            </h2>
                                        </div>
                                        <div className="commission-modal-body">
                                        <form
                                            onSubmit={handleSubmitStatusForm(
                                                handleEditBulkStatus
                                            )}
                                        >
                                            <div>
                                                    <div className="commission-modal-label">
                                                        Fast Commission Status
                                                </div>
                                                <select
                                                        className="commission-modal-select"
                                                    name="fastComm"
                                                    ref={registerStatusForm({
                                                        required: false,
                                                    })}
                                                    placeholder="Status"
                                                    type="string"
                                                >
                                                    <option value="50">
                                                        Fast Comm (50%)
                                                    </option>
                                                    <option value="100">
                                                        Full Comm (100%)
                                                    </option>
                                                </select>
                                            </div>
                                                <button className="commission-modal-button">
                                                Submit
                                            </button>
                                        </form>
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {bulkChangeCommissionStatusModal && (
                            <Modal reset={resetBulkUplineCommissionStatusModal}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container">
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                                Bulk Overriding Commission
                                            </h2>
                                        </div>
                                        <div className="commission-modal-body">
                                        <form
                                            onSubmit={handleSubmitStatusForm(
                                                handleEditBulkUplineStatus
                                            )}
                                        >
                                            <div>
                                                    <div className="commission-modal-label">
                                                        Fast Commission Status
                                                </div>
                                                <select
                                                        className="commission-modal-select"
                                                    name="fastComm"
                                                    ref={registerStatusForm({
                                                        required: false,
                                                    })}
                                                    placeholder="Status"
                                                    type="string"
                                                >
                                                    <option value="0">
                                                        None (0%)
                                                    </option>
                                                    <option value="100">
                                                        Full Comm (100%)
                                                    </option>
                                                </select>
                                            </div>
                                                <button className="commission-modal-button">
                                                Submit
                                            </button>
                                        </form>
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        )}


                        {changeAgentCommissionStatusModal && (
                            <Modal reset={resetAgentCommissionStatusModal}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container">
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                            Change Commission Status
                                            </h2>
                                        </div>
                                        <div className="commission-modal-body">
                                        <form
                                            onSubmit={handleSubmitStatusForm(
                                                typeReferralCommissionStatus
                                                    ? handleEditLeaderStatus
                                                    : handleEditStatus
                                            )}
                                        >
                                            <div>
                                                    <div className="commission-modal-label">
                                                        Fast Commission Status
                                                </div>
                                                <select
                                                        className="commission-modal-select"
                                                    name="fastComm"
                                                    ref={registerStatusForm({
                                                        required: false,
                                                    })}
                                                    placeholder="Status"
                                                    type="string"
                                                >
                                                    <option value="50">
                                                        Fast Comm (50%)
                                                    </option>
                                                    <option value="100">
                                                        Full Comm (100%)
                                                    </option>
                                                </select>
                                            </div>
                                                <button className="commission-modal-button">
                                                Submit
                                            </button>
                                        </form>
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        )}

                        {commissionState && (
                            <Modal reset={resetCommissionState}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container" style={{ maxHeight: '90vh', width: '800px', maxWidth: '95vw' }}>
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                                Commission Details
                                            </h2>
                                        <div className="flex justify-end">
                                            <FiX
                                                onClick={() => {
                                                    setCommissionState(false);
                                                }}
                                                className="cursor-pointer"
                                            />
                                        </div>
                                        </div>
                                        <div className="commission-modal-body" style={{ maxHeight: 'calc(90vh - 80px)', overflowY: 'auto', paddingRight: '10px' }}>
                                        {commissionFields.map((item, i) => {
                                            if (
                                                item.name === "purchaserName" ||
                                                item.name === "purchaserIc" ||
                                                item.name === "purchaserContact"
                                            ) {
                                                return (
                                                    <div
                                                        className="flex row"
                                                        key={i}
                                                    >
                                                        <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium">
                                                            {item.label} :
                                                        </div>
                                                        <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs capitalize">
                                                            {modalData.purchaser
                                                                ? modalData
                                                                      .purchaser[
                                                                      item.name
                                                                  ]
                                                                : ""}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div
                                                    className="flex row"
                                                    key={item.name}
                                                        style={{ marginBottom: '12px' }}
                                                    >
                                                        <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium whitespace-nowrap pr-4">
                                                            {item.name === "leadercommissionPercentage" 
                                                                ? "Leader Commission %" 
                                                                : item.name === "primeleadercommissionPercentage"
                                                                ? "Prime Leader Commission %"
                                                                : item.label} :
                                                    </div>
                                                        <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs capitalize">
                                                        {item.type ===
                                                            "numbers" &&
                                                        item.label !==
                                                            "Commission Percentage" &&
                                                        item.label !==
                                                            "Leader Commission Percentage" &&
                                                        item.label !==
                                                            "Prime Commission Percentage"
                                                            ? `RM ${NumberFormatter.format(
                                                                  modalData[
                                                                      item.name
                                                                  ]
                                                              )}`
                                                            : `${
                                                                  modalData[
                                                                      item.name
                                                                  ]
                                                              } %`}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        </div>
                                    </div>
                                </div>
                            </Modal>
            )}
        </>
    );
};

export default withUser(FastCases);